import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTheme } from '@material-ui/core';

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const ChartsDate = () => {
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;
    const isDarkMode = theme.palette.type === 'dark';

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    // Gerar cor com transparência baseada no tema
    const getColorWithOpacity = (color, opacity) => {
        // Converter hex para rgba ou usar color diretamente se já for rgba
        if (color.startsWith('#')) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        // Se a cor já for rgba, tenta extrair valores e substituir opacidade
        const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/);
        if (rgbaMatch) {
            return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
        }
        // Fallback para cor original
        return color;
    };

    // Configurações do gráfico adaptadas ao tema
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                titleColor: isDarkMode ? '#f1f5f9' : '#0f172a',
                bodyColor: isDarkMode ? '#94a3b8' : '#64748b',
                borderColor: isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.08)',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 10,
                displayColors: false,
                callbacks: {
                    title: (context) => context[0].label,
                    label: (context) => `Total: ${context.raw}`,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    maxRotation: 0,
                    minRotation: 0,
                    font: { size: 11, weight: '500' },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(15, 23, 42, 0.06)',
                    drawBorder: false,
                },
                ticks: {
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    padding: 8,
                    font: { size: 11 },
                },
            },
        },
    };

    // Dados do gráfico com cores adaptadas ao tema
    const dataCharts = {
        labels: ticketsData?.data.length > 0 ? ticketsData.data.map((item) => (
            item.hasOwnProperty('horario') ? `${item.horario}:00-${item.horario}:59` : item.data
        )) : [],
        datasets: [
            {
                data: ticketsData?.data.length > 0 ? ticketsData.data.map(item => item.total) : [],
                backgroundColor: (context) => {
                    const { chart } = context;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return getColorWithOpacity(theme.palette.primary.main, 0.85);
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, getColorWithOpacity(theme.palette.primary.main, 0.95));
                    gradient.addColorStop(1, getColorWithOpacity(theme.palette.primary.main, 0.35));
                    return gradient;
                },
                hoverBackgroundColor: theme.palette.primary.main,
                borderWidth: 0,
                borderRadius: 12,
                borderSkipped: false,
                barThickness: 24,
                maxBarThickness: 32,
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            setIsLoading(true);
            const { data } = await api.get(`/dashboard/ticketsDay?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Error al buscar información de los tickets');
        } finally {
            setIsLoading(false);
        }
    };

    // Estilos considerando tema claro e escuro
    const styles = {
        container: {
            padding: 0,
            borderRadius: 12,
            backgroundColor: "transparent",
        },
        countBadge: {
            marginLeft: 8,
            padding: '4px 8px',
            backgroundColor: getColorWithOpacity(theme.palette.primary.main, 0.15),
            borderRadius: 4,
            fontSize: '0.875rem',
            color: theme.palette.primary.main,
        },
        chartContainer: {
            height: 320,
            position: 'relative',
            marginTop: 8,
            padding: 16,
            borderRadius: 12,
            backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
            border: isDarkMode ? '1px solid rgba(148,163,184,0.12)' : '1px solid rgba(15,23,42,0.06)',
        },
        emptyState: {
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
            borderRadius: 12,
            border: isDarkMode ? '1px solid rgba(148,163,184,0.12)' : '1px solid rgba(15,23,42,0.06)',
        },
        filterButton: {
            textTransform: 'none',
            borderRadius: 6,
            padding: '8px 16px',
        },
        datePickerContainer: {
            marginBottom: 24,
            marginTop: 8,
        }
    };

    return (
        <Paper style={styles.container} elevation={isDarkMode ? 2 : 1}>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                {i18n.t("dashboard.users.totalAttendances")} 
                <span style={styles.countBadge}>
                    {ticketsData?.count || 0}
                </span>
            </Typography>

            <Grid container spacing={2} style={styles.datePickerContainer}>
                <Grid item xs={12} sm={4} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => setInitialDate(newValue)}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => <TextField fullWidth {...params} variant="outlined" size="small" />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => setFinalDate(newValue)}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => <TextField fullWidth {...params} variant="outlined" size="small" />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4} md="auto" style={{ display: 'flex', alignItems: 'center' }}>
                    <Button 
                        onClick={handleGetTicketsInformation} 
                        variant="contained" 
                        color="primary"
                        disabled={isLoading}
                        style={styles.filterButton}
                    >
                        {isLoading ? i18n.t("mainDrawer.loading") : i18n.t("mainDrawer.filter")}
                    </Button>
                </Grid>
            </Grid>

            {ticketsData?.data.length === 0 ? (
                <div style={styles.emptyState}>
                    <Typography style={{ color: isDarkMode ? "#cbd5e1" : undefined }}>
                      {i18n.t("dashboard.emptyState.noChartData")}
                    </Typography>
                </div>
            ) : (
                <div style={styles.chartContainer}>
                    <Bar options={options} data={dataCharts} />
                </div>
            )}
        </Paper>
    );
};

export default ChartsDate;